import {
  getColumnsInViewport,
  itemsChanged,
  RowAtPositionFunc,
} from "@vuu-ui/vuu-utils";
import type { VuuRange } from "@vuu-ui/vuu-protocol-types";
import {
  ForwardedRef,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ViewportMeasurements } from "./useTableViewport";
import {
  getRowElementByAriaIndex,
  howFarIsRowOutsideViewport,
} from "./table-dom-utils";
import type { RuntimeColumnDescriptor } from "@vuu-ui/vuu-table-types";
import { ICellFocusState } from "./CellFocusState";

export type ScrollDirectionVertical = "up" | "down";
export type ScrollDirectionHorizontal = "left" | "right";
export type ScrollDirection =
  | ScrollDirectionVertical
  | ScrollDirectionHorizontal;

/**
 * scroll into view the row at given pixel offset.
 */
export interface ScrollRequestPosition {
  instant?: boolean;
  scrollPos: number;
  type: "scroll-top" | "scroll-bottom";
}

/**
 * scroll into view the row at given row index posiiton.
 */
export interface ScrollRequestRow {
  rowIndex: number;
  type: "scroll-row";
}
export interface ScrollRequestEnd {
  type: "scroll-end";
  direction: "home" | "end";
}

export interface ScrollRequestPage {
  type: "scroll-page";
  direction: ScrollDirectionVertical;
}

export type ScrollRequest =
  | ScrollRequestPage
  | ScrollRequestEnd
  | ScrollRequestRow
  | ScrollRequestPosition;

export type ScrollRequestHandler = (request: ScrollRequest) => void;

export interface ScrollingAPI {
  /**
   * Scroll the row at the given (zero based) index into view.
   */
  scrollToIndex: (itemIndex: number) => void;
  scrollToKey: (rowKey: string) => void;
}

/** How far we allow horizontal scroll movement before we recheck the rendered columns */
const SCROLL_MOVE_CHECK_THRESHOLD = 100;

/** The buffer size in pixels that we allow for rendering columns just outside the viewport */
const HORIZONTAL_SCROLL_BUFFER = 200;

/**
 * Return the maximum scroll positions for gioven container
 * @param container
 * @returns [maxScrollLeft, maxScrollTop]
 */
const getMaxScroll = (container: HTMLElement) => {
  const { clientHeight, clientWidth, scrollHeight, scrollWidth } = container;
  return [scrollWidth - clientWidth, scrollHeight - clientHeight];
};

/**
 * scrollTop, scrollHeight and clientHeight are rounded by the browser, so at
 * the very top or end of the scroll range they may disagree by a pixel or so,
 * depending on the display's devicePixelRatio. With virtualised scrolling, one
 * pixel of scroll can represent hundreds of rows, so treat positions this close
 * to either end as being exactly at that end.
 */
const SCROLL_END_TOLERANCE = 2;

export const getPctScrollTop = (scrollTop: number, maxScrollTop: number) => {
  if (maxScrollTop <= 0 || scrollTop < SCROLL_END_TOLERANCE) {
    return 0;
  } else if (maxScrollTop - scrollTop < SCROLL_END_TOLERANCE) {
    return 1;
  } else {
    return scrollTop / maxScrollTop;
  }
};

const getPctScroll = (container: HTMLElement) => {
  const {
    clientHeight,
    clientWidth,
    scrollHeight,
    scrollLeft,
    scrollTop,
    scrollWidth,
  } = container;

  const maxScrollLeft = scrollWidth - clientWidth;
  const pctScrollLeft = scrollLeft / (scrollWidth - clientWidth);
  const maxScrollTop = scrollHeight - clientHeight;
  const pctScrollTop = getPctScrollTop(scrollTop, maxScrollTop);

  return [
    scrollLeft,
    pctScrollLeft,
    maxScrollLeft,
    scrollTop,
    pctScrollTop,
    maxScrollTop,
  ];
};

/**
 * Positions closer than this are considered in sync. When browser is zoomed,
 * scroll positions snap to device pixels, so are often fractional.
 */
const SCROLL_SYNC_TOLERANCE = 1;

const getProportionalScrollPosition = (
  sourcePos: number,
  sourceMax: number,
  targetMax: number,
) => (sourceMax > 0 ? (sourcePos / sourceMax) * targetMax : 0);

type ScrollCoordinates = { left: number; top: number };

/**
 * Scroll target to the same proportional position as source, unless already
 * there. Comparing positions, rather than tracking which container initiated a
 * scroll, means we are not thrown out of sync by scroll events the browser
 * itself fires (e.g. on zoom). Returns the position target was scrolled to, if
 * any, so the resulting scroll event on target can be recognised as an echo.
 */
const syncScrollPosition = (
  source: HTMLElement,
  target: HTMLElement,
): ScrollCoordinates | undefined => {
  const [sourceMaxLeft, sourceMaxTop] = getMaxScroll(source);
  const [targetMaxLeft, targetMaxTop] = getMaxScroll(target);
  const left = Math.round(
    getProportionalScrollPosition(
      source.scrollLeft,
      sourceMaxLeft,
      targetMaxLeft,
    ),
  );
  const top = getProportionalScrollPosition(
    source.scrollTop,
    sourceMaxTop,
    targetMaxTop,
  );
  if (
    Math.abs(target.scrollLeft - left) >= SCROLL_SYNC_TOLERANCE ||
    Math.abs(target.scrollTop - top) >= SCROLL_SYNC_TOLERANCE
  ) {
    target.scrollTo({ left, top, behavior: "auto" });
    return { left, top };
  }
};

/**
 * A scroll event on a container we have just scrolled programmatically must not
 * be synced back to the other container. Some browsers (e.g. WebKit) animate a
 * mouse wheel scroll, so by the time the echo event arrives, the source may have
 * scrolled further. Syncing back would cancel the animation.
 */
const isEchoScroll = (
  el: HTMLElement,
  expectedPosRef: { current: ScrollCoordinates | undefined },
) => {
  const { current: expectedPos } = expectedPosRef;
  expectedPosRef.current = undefined;
  return (
    expectedPos !== undefined &&
    Math.abs(el.scrollLeft - expectedPos.left) < SCROLL_SYNC_TOLERANCE &&
    Math.abs(el.scrollTop - expectedPos.top) < SCROLL_SYNC_TOLERANCE
  );
};

export const noScrolling: ScrollingAPI = {
  scrollToIndex: () => undefined,
  scrollToKey: () => undefined,
};

interface CallbackRefHookProps<T = HTMLElement> {
  onAttach?: (el: T) => void;
  onDetach: (el: T) => void;
  label?: string;
}

const useCallbackRef = <T = HTMLElement>({
  onAttach,
  onDetach,
}: CallbackRefHookProps<T>) => {
  const ref = useRef<T | null>(null);
  const callbackRef = useCallback(
    (el: T | null) => {
      if (el) {
        ref.current = el;
        onAttach?.(el);
      } else if (ref.current) {
        const { current: originalRef } = ref;
        ref.current = el;
        onDetach?.(originalRef);
      }
    },
    [onAttach, onDetach],
  );
  return callbackRef;
};

type ScrollPos = {
  scrollLeft: number;
  scrollTop: number;
};

export interface TableScrollHookProps {
  cellFocusStateRef: RefObject<ICellFocusState>;
  columns: RuntimeColumnDescriptor[];
  getRowAtPosition: RowAtPositionFunc;
  onHorizontalScroll?: (scrollLeft: number) => void;
  onVerticalScroll?: (scrollTop: number, pctScrollTop: number) => void;
  /**
   * When we have a virtualized scroll container, keyboard navigation is
   * performed `in situ`. We shift the range of rows rendered within the
   * viewport, whithout actually moving the scroll position
   */
  onVerticalScrollInSitu?: (rowIndexOffsetCount: number) => void;
  rowHeight: number;
  /**
   * Total number of rows in the dataset
   */
  rowCount: number;
  scrollingApiRef?: ForwardedRef<ScrollingAPI>;
  setRange: (range: VuuRange) => void;
  showPaginationControls?: boolean;
  viewportMeasurements: ViewportMeasurements;
}

export const useTableScroll = ({
  cellFocusStateRef,
  columns,
  getRowAtPosition,
  onHorizontalScroll,
  onVerticalScroll,
  onVerticalScrollInSitu,
  rowHeight,
  rowCount,
  scrollingApiRef,
  setRange,
  viewportMeasurements,
}: TableScrollHookProps) => {
  const firstRowRef = useRef<number>(0);
  const rowHeightRef = useRef(rowHeight);
  const contentContainerPosRef = useRef<ScrollPos>({
    scrollTop: 0,
    scrollLeft: 0,
  });
  const scrollbarContainerRef = useRef<HTMLDivElement | null>(null);
  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const lastHorizontalScrollCheckPoint = useRef(0);

  const {
    appliedPageSize,
    contentHeight,
    isVirtualScroll,
    rowCount: viewportRowCount,
    totalHeaderHeight,
    usesMeasuredHeaderHeight,
    viewportBodyHeight,
    viewportWidth,
  } = viewportMeasurements;

  const columnsWithinViewportRef = useRef<RuntimeColumnDescriptor[]>([]);
  const [, forceRefresh] = useState({});

  const preSpanRef = useRef(0);

  useMemo(() => {
    const [visibleColumns, offset] = getColumnsInViewport(
      columns,
      contentContainerPosRef.current.scrollLeft,
      contentContainerPosRef.current.scrollLeft +
      viewportWidth +
      HORIZONTAL_SCROLL_BUFFER,
    );
    preSpanRef.current = offset;
    columnsWithinViewportRef.current = visibleColumns;
  }, [viewportWidth, columns]);

  const handleHorizontalScroll = useCallback(
    (scrollLeft: number) => {
      contentContainerPosRef.current.scrollLeft = scrollLeft;
      onHorizontalScroll?.(scrollLeft);

      if (
        Math.abs(scrollLeft - lastHorizontalScrollCheckPoint.current) >
        SCROLL_MOVE_CHECK_THRESHOLD
      ) {
        lastHorizontalScrollCheckPoint.current = scrollLeft;

        const [visibleColumns, pre] = getColumnsInViewport(
          columns,
          scrollLeft,
          scrollLeft + viewportWidth + HORIZONTAL_SCROLL_BUFFER,
        );

        if (itemsChanged(columnsWithinViewportRef.current, visibleColumns)) {
          preSpanRef.current = pre;
          columnsWithinViewportRef.current = visibleColumns;
          forceRefresh({});
        }
      }
    },
    [columns, onHorizontalScroll, viewportWidth],
  );
  const handleVerticalScroll = useCallback(
    (scrollTop: number, pctScrollTop: number) => {
      contentContainerPosRef.current.scrollTop = scrollTop;
      const { current: prevFirstRow } = firstRowRef;

      onVerticalScroll?.(scrollTop, pctScrollTop);
      const firstRow = getRowAtPosition(scrollTop);
      if (firstRow !== prevFirstRow) {
        firstRowRef.current = firstRow;
        const lastRow = firstRow + viewportRowCount;
        setRange({ from: firstRow, to: lastRow });

        // If we've scrolled the focused cell out of view, we need to remove
        // focus from it. The row element will be recycled and used as the
        // render target for a different DataRow, we do not want the focus
        // state of the cell to be preserved.
        // Conversely, if we scroll the focussed cell back into the viewport,
        // we must re-apply focus to it. We use the placeholder cell for this.
        const { current: focusState } = cellFocusStateRef;
        if (focusState.cellPos) {
          const prevLastRow = prevFirstRow + viewportRowCount;
          const [row] = focusState.cellPos;
          const wasInViewport = row >= prevFirstRow && row < prevLastRow;
          const isInViewport = row >= firstRow && row < lastRow;

          if (wasInViewport && !isInViewport) {
            focusState.placeholderEl?.focus({ preventScroll: true });
            focusState.outsideViewport = row < firstRow ? "above" : "below";
          } else if (isInViewport && !wasInViewport) {
            focusState.outsideViewport = false;
            // focusCell?.(focusState.cellPos);
          }
        }
      }
      onVerticalScrollInSitu?.(0);
    },
    [
      cellFocusStateRef,
      // focusCell,
      getRowAtPosition,
      onVerticalScroll,
      onVerticalScrollInSitu,
      setRange,
      viewportRowCount,
    ],
  );

  // Because of the amount of work done when we rerender a virtualised section
  // we may drop scroll events. Make sure we get the final resting position right
  // by remeasuring after a short delay.
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
  // positions we have programmatically scrolled each container to
  const expectedContentPosRef = useRef<ScrollCoordinates>(undefined);
  const expectedScrollbarPosRef = useRef<ScrollCoordinates>(undefined);

  const checkScrollbarScrollPosition = useCallback(() => {
    const { current: contentContainer } = contentContainerRef;
    const { current: scrollbarContainer } = scrollbarContainerRef;
    if (scrollbarContainer && contentContainer) {
      expectedContentPosRef.current = syncScrollPosition(
        scrollbarContainer,
        contentContainer,
      );
    }
    scrollTimerRef.current = null;
  }, []);

  const handleScrollbarContainerScroll = useCallback(() => {
    const { current: contentContainer } = contentContainerRef;
    const { current: scrollbarContainer } = scrollbarContainerRef;

    if (contentContainer && scrollbarContainer) {
      if (!isEchoScroll(scrollbarContainer, expectedScrollbarPosRef)) {
        expectedContentPosRef.current = syncScrollPosition(
          scrollbarContainer,
          contentContainer,
        );
        if (scrollTimerRef.current) {
          clearTimeout(scrollTimerRef.current);
        }
        scrollTimerRef.current = setTimeout(checkScrollbarScrollPosition, 60);
      }
    }

    onVerticalScrollInSitu?.(0);
  }, [checkScrollbarScrollPosition, onVerticalScrollInSitu]);

  const handleContentContainerScroll = useCallback(() => {
    const { current: contentContainer } = contentContainerRef;
    const { current: scrollbarContainer } = scrollbarContainerRef;
    const { current: scrollPos } = contentContainerPosRef;

    if (contentContainer && scrollbarContainer) {
      const [scrollLeft, , , scrollTop, pctScrollTop] =
        getPctScroll(contentContainer);

      if (!isEchoScroll(contentContainer, expectedContentPosRef)) {
        expectedScrollbarPosRef.current = syncScrollPosition(
          contentContainer,
          scrollbarContainer,
        );
      }

      if (scrollPos.scrollTop !== scrollTop) {
        handleVerticalScroll(scrollTop, pctScrollTop);
      }
      if (scrollPos.scrollLeft !== scrollLeft) {
        handleHorizontalScroll(scrollLeft);
      }
    }
  }, [handleVerticalScroll, handleHorizontalScroll]);

  const handleAttachScrollbarContainer = useCallback(
    (el: HTMLDivElement) => {
      scrollbarContainerRef.current = el;
      el.addEventListener("scroll", handleScrollbarContainerScroll, {
        passive: true,
      });
    },
    [handleScrollbarContainerScroll],
  );

  const handleDetachScrollbarContainer = useCallback(
    (el: HTMLDivElement) => {
      scrollbarContainerRef.current = null;
      el.removeEventListener("scroll", handleScrollbarContainerScroll);
    },
    [handleScrollbarContainerScroll],
  );

  const handleAttachContentContainer = useCallback(
    (el: HTMLDivElement) => {
      contentContainerRef.current = el;
      el.addEventListener("scroll", handleContentContainerScroll, {
        passive: true,
      });
    },
    [handleContentContainerScroll],
  );

  const handleDetachContentContainer = useCallback(
    (el: HTMLDivElement) => {
      contentContainerRef.current = null;
      el.removeEventListener("scroll", handleContentContainerScroll);
    },
    [handleContentContainerScroll],
  );

  const contentContainerCallbackRef = useCallbackRef({
    onAttach: handleAttachContentContainer,
    onDetach: handleDetachContentContainer,
  });

  const scrollbarContainerCallbackRef = useCallbackRef({
    onAttach: handleAttachScrollbarContainer,
    onDetach: handleDetachScrollbarContainer,
  });

  const requestScroll: ScrollRequestHandler = useCallback(
    (scrollRequest) => {
      const { current: contentContainer } = contentContainerRef;
      const { current: scrollbarContainer } = scrollbarContainerRef;

      if (contentContainer) {
        const [maxScrollLeft, maxScrollTop] = getMaxScroll(contentContainer);
        const { scrollLeft, scrollTop } = contentContainer;

        /**
         * With a virtualised scroll, keyboard navigation shifts the rendered rows
         * within the viewport without moving the scroll position. Once we reach
         * the first or last page, we scroll to top or end instead, so rows are
         * correctly aligned and the in situ offset is reset.
         */
        const scrollInSitu = (offset: number) => {
          const firstRow = firstRowRef.current + offset;
          const maxFirstRow = Math.max(0, rowCount - viewportRowCount);
          if (firstRow <= 0 || firstRow >= maxFirstRow) {
            const top = firstRow <= 0 ? 0 : maxScrollTop;
            if (Math.abs(scrollTop - top) < SCROLL_SYNC_TOLERANCE) {
              // no scroll event will fire, reset rows for current position
              handleVerticalScroll(
                scrollTop,
                getPctScrollTop(scrollTop, maxScrollTop),
              );
            } else {
              contentContainer.scrollTo({
                top,
                left: scrollLeft,
                behavior: "instant",
              });
            }
          } else {
            onVerticalScrollInSitu?.(offset);
            firstRowRef.current = firstRow;
            setRange({ from: firstRow, to: firstRow + viewportRowCount });
          }
        };

        switch (scrollRequest.type) {
          case "scroll-top":
            {
              // special case for setting scroll position immediately back to top
              if (
                scrollRequest.instant &&
                scrollRequest.scrollPos === 0 &&
                scrollbarContainer
              ) {
                contentContainer.scrollTop = 0;
                scrollbarContainer.scrollTop = 0;
              } else {
                contentContainer.scrollTo({
                  top: scrollRequest.scrollPos,
                  left: scrollLeft,
                  behavior: "instant",
                });
              }
            }
            break;
          case "scroll-bottom":
            {
              contentContainer.scrollTo({
                top:
                  scrollRequest.scrollPos -
                  (viewportBodyHeight - 2 * rowHeight),
                left: scrollLeft,
                behavior: "instant",
              });
            }
            break;
          case "scroll-row":
            {
              const activeRow = getRowElementByAriaIndex(
                contentContainer,
                scrollRequest.rowIndex,
              );

              if (activeRow !== null) {
                const [direction, distance] = howFarIsRowOutsideViewport(
                  activeRow,
                  totalHeaderHeight,
                );
                if (direction && distance) {
                  if (isVirtualScroll) {
                    scrollInSitu(direction === "down" ? 1 : -1);
                  } else {
                    let newScrollLeft = scrollLeft;
                    let newScrollTop = scrollTop;
                    if (direction === "up" || direction === "down") {
                      newScrollTop = Math.min(
                        Math.max(0, scrollTop + distance),
                        maxScrollTop,
                      );
                    } else {
                      newScrollLeft = Math.min(
                        Math.max(0, scrollLeft + distance),
                        maxScrollLeft,
                      );
                    }
                    contentContainer.scrollTo({
                      top: newScrollTop,
                      left: newScrollLeft,
                      // avoid behaviour: 'smooth', doesn't work correctly
                      behavior: "instant",
                    });
                  }
                }
              }
            }
            break;
          case "scroll-page":
            {
              const { direction } = scrollRequest;
              if (isVirtualScroll) {
                scrollInSitu(
                  direction === "down" ? viewportRowCount : -viewportRowCount,
                );
              } else {
                const scrollBy =
                  direction === "down" ? appliedPageSize : -appliedPageSize;
                const newScrollTop = Math.min(
                  Math.max(0, scrollTop + scrollBy),
                  maxScrollTop,
                );
                contentContainer.scrollTo({
                  top: newScrollTop,
                  left: scrollLeft,
                  behavior: "auto",
                });
              }
            }
            break;
          case "scroll-end":
            {
              const { direction } = scrollRequest;
              const scrollTo = direction === "end" ? maxScrollTop : 0;

              contentContainer.scrollTo({
                top: scrollTo,
                left: contentContainer.scrollLeft,
                behavior: "auto",
              });
            }
            break;
          default:
            console.warn(`unexpected scroll request ${scrollRequest["type"]}`);
        }
      }
    },
    [
      appliedPageSize,
      handleVerticalScroll,
      isVirtualScroll,
      onVerticalScrollInSitu,
      rowCount,
      rowHeight,
      setRange,
      totalHeaderHeight,
      viewportBodyHeight,
      viewportRowCount,
    ],
  );

  const scrollHandles: ScrollingAPI = useMemo(
    () => ({
      /**
       * If row is not fully within the viewport, scroll so that it is the
       * first row in the viewport (or as near as possible, for the last rows).
       */
      scrollToIndex: (rowIndex: number) => {
        const { current: contentContainer } = contentContainerRef;
        const { current: firstVisibleRow } = firstRowRef;
        // The last row in viewport may be partially visible
        const fullyVisibleRowCount = Math.max(1, viewportRowCount - 1);
        if (
          contentContainer === null ||
          (rowIndex >= firstVisibleRow &&
            rowIndex < firstVisibleRow + fullyVisibleRowCount)
        ) {
          return;
        }
        const firstRow = Math.min(
          Math.max(0, rowIndex),
          Math.max(0, rowCount - viewportRowCount),
        );
        const [, maxScrollTop] = getMaxScroll(contentContainer);
        // When scrolling is virtualised, the first row at a given scroll position
        // is offset by a proportion of the virtualised extent (see
        // virtualRowPositioning), so we invert that calculation here.
        const virtualisedExtent = isVirtualScroll
          ? rowCount * rowHeight - contentHeight
          : 0;
        const scrollTop =
          maxScrollTop > 0
            ? (firstRow * rowHeight * maxScrollTop) /
              (maxScrollTop + virtualisedExtent)
            : 0;
        contentContainer.scrollTo({
          top: Math.min(scrollTop, maxScrollTop),
          left: contentContainer.scrollLeft,
          behavior: "instant",
        });
      },
      scrollToKey: (rowKey: string) => {
        console.log(`scrollToKey ${rowKey}`);
      },
    }),
    [contentHeight, isVirtualScroll, rowCount, rowHeight, viewportRowCount],
  );

  useImperativeHandle(scrollingApiRef, () => {
    if (scrollbarContainerRef.current) {
      return scrollHandles;
    } else {
      return noScrolling;
    }
  }, [scrollHandles]);

  useEffect(() => {
    if (rowHeight !== rowHeightRef.current) {
      rowHeightRef.current = rowHeight;
      if (contentContainerPosRef.current.scrollTop > 0) {
        if (contentContainerRef.current) {
          contentContainerRef.current.scrollTop = 0;
        }
      }
    } else if (usesMeasuredHeaderHeight) {
      const { current: from } = firstRowRef;
      const rowRange = { from, to: from + viewportRowCount };
      setRange(rowRange);
    }
  }, [rowHeight, setRange, usesMeasuredHeaderHeight, viewportRowCount]);

  return {
    columnsWithinViewport: columnsWithinViewportRef.current,
    /** Ref to be assigned to ScrollbarContainer */
    scrollbarContainerRef: scrollbarContainerCallbackRef,
    /** Ref to be assigned to ContentContainer */
    contentContainerRef: contentContainerCallbackRef,
    /** Scroll the table  */
    requestScroll,
    scrollTop: contentContainerPosRef.current.scrollTop,
    /** number of leading columns not rendered because of virtualization  */
    virtualColSpan: preSpanRef.current,
  };
};
